import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TailwindWrapper } from '@utils/TailwindWrapper';
import { Text, Button, Input, Dialog } from '@ui-component-library';

import { getShapeInfo } from '../../../../api/services/ad';

import SellTimberVirkesResponsiveTable, { sectionsUseExtraSpecies } from './components/SellTimberVirkesResponsiveTable';
import { SELL_TIMBER_FIELD_INFO, SellTimberFieldInfo } from './components/SellTimberFieldInfo.js';
import SellTimberMap, {
  SELL_TIMBER_VIRKES_LABEL_MIN_ZOOM_STEP3
} from './components/SellTimberMap';
import {
  buildSkeletonVirkesRowsFromMapSelection,
  collectFeaturesFromMapSelection
} from './helpers/sellTimberMapSelection';
import {
  formatSvLocaleNumber,
  nextSellTimberSectionId,
  roundHa,
  SELL_TIMBER_EXTRA_SPECIES,
  SELL_TIMBER_SPECIES_TGL,
  excludeGrotMarkerFromTimberPlacedMarkers
} from './helpers/sellTimberShared';

function emptyForm() {
  const tgl = Object.fromEntries(SELL_TIMBER_SPECIES_TGL.map(({ key }) => [key, '']));
  return {
    name: '',
    area: '',
    volumeM3sk: '',
    volumeM3fub: '',
    ...tgl
  };
}

function sectionToForm(row) {
  const tgl = Object.fromEntries(
    SELL_TIMBER_SPECIES_TGL.map(({ key }) => [
      key,
      row[key] !== '' && row[key] != null ? String(row[key]) : ''
    ])
  );
  return {
    name: row.name != null ? String(row.name) : '',
    area: String(
      roundHa(row.area, { invalidAsZero: true, invalidValue: '', invalidNumberAsOriginal: true })
    ),
    volumeM3sk: row.volumeM3sk !== '' && row.volumeM3sk != null ? String(row.volumeM3sk) : '',
    volumeM3fub: row.volumeM3fub !== '' && row.volumeM3fub != null ? String(row.volumeM3fub) : '',
    ...tgl
  };
}

/**
 * Map `/map-api-data/` payload to table row — fractions 0–1 for TGL like AdFlow Step2.
 * @param {string | null | undefined} nameFromMapSelection — label from step 2 selection when API omits `name`.
 */
/**
 * Virkesrad from Skogsbruksplan-avdelning (same fields as {@link DataTab} / property `sections` bundle).
 * Uses planens volym, areal och TGL — not `/map-api-data/` geometry inference.
 */
function sbpSectionToVirkesRow(section, existingRows, nameFromMapSelection) {
  const volSk =
    section.volume != null && section.volume !== '' ? Math.round(Number(section.volume)) : 0;
  const volFub = Math.round(volSk * 0.83);
  const index = existingRows.length;
  const nameFromSelection =
    nameFromMapSelection != null && String(nameFromMapSelection) !== ''
      ? String(nameFromMapSelection)
      : '';
  const local = section.localID != null ? String(section.localID) : '';
  return {
    id: nextSellTimberSectionId(existingRows),
    name: local ? `Avdelning ${local}` : nameFromSelection || `Avdelning ${index + 1}`,
    area: roundHa(section.area, { invalidAsZero: true }),
    volumeM3sk: volSk,
    volumeM3fub: volFub,
    tall: Math.round(Number(section.tall)) || 0,
    gran: Math.round(Number(section.gran)) || 0,
    lov: Math.round(Number(section.lov)) || 0,
    ...Object.fromEntries(SELL_TIMBER_EXTRA_SPECIES.map(({ key }) => [key, '']))
  };
}

function mapShapeInfoDataToRow(data, existingRows, nameFromMapSelection) {
  const fracToPct = (v) => {
    if (v == null || v === '') return 0;
    const n = Number(v);
    if (Number.isNaN(n)) return 0;
    return n > 1 ? Math.round(n) : Math.round(n * 100);
  };
  const fracToPctOpt = (v) => {
    if (v == null || v === '') return '';
    const n = Number(v);
    if (Number.isNaN(n)) return '';
    return n > 1 ? Math.round(n) : Math.round(n * 100);
  };

  const volSk = data.volume != null ? Math.round(Number(data.volume)) : 0;
  const volFub =
    data.volume_m3fub != null && data.volume_m3fub !== ''
      ? Math.round(Number(data.volume_m3fub))
      : Math.round(volSk * 0.83);

  const index = existingRows.length;
  const nameFromApi = data.name != null && String(data.name) !== '' ? String(data.name) : '';
  const nameFromSelection =
    nameFromMapSelection != null && String(nameFromMapSelection) !== ''
      ? String(nameFromMapSelection)
      : '';
  return {
    id: nextSellTimberSectionId(existingRows),
    name: nameFromApi || nameFromSelection || `Avdelning ${index + 1}`,
    area: data.area != null && data.area !== '' ? roundHa(data.area, { invalidAsZero: true }) : 0,
    volumeM3sk: volSk,
    volumeM3fub: volFub,
    tall: fracToPct(data.tall),
    gran: fracToPct(data.gran),
    lov: fracToPct(data.lov),
    al: fracToPctOpt(data.al),
    asp: fracToPctOpt(data.asp),
    björk: fracToPctOpt(data.björk),
    bok: fracToPctOpt(data.bok),
    ek: fracToPctOpt(data.ek)
  };
}

const TGL_PCT_KEYS = SELL_TIMBER_SPECIES_TGL.map(({ key }) => key);

function parsePctField(v) {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  if (Number.isNaN(n)) return null;
  return n;
}

function hasNegativeTglPercent(form) {
  return TGL_PCT_KEYS.some((k) => {
    const n = parsePctField(form[k]);
    return n !== null && n < 0;
  });
}

function isNegativeNumberFieldValue(v) {
  const n = parsePctField(v);
  return n !== null && n < 0;
}

const AREA_VOLUME_KEYS = ['area', 'volumeM3sk', 'volumeM3fub'];

function hasNegativeAreaOrVolume(form) {
  return AREA_VOLUME_KEYS.some((k) => {
    const n = parsePctField(form[k]);
    return n !== null && n < 0;
  });
}

/** m³sk empty, unparseable, or <= 0 (not strictly positive). */
function isVolumeM3skNotPositive(form) {
  const raw = form.volumeM3sk;
  if (raw === '' || raw == null) return true;
  const n = parseFloat(String(raw).replace(',', '.'));
  if (Number.isNaN(n)) return true;
  return n <= 0;
}

/** Modal save: negative area or any volume field, or m³sk not strictly positive. */
function hasModalAreaVolumeValidationError(form) {
  return hasNegativeAreaOrVolume(form) || isVolumeM3skNotPositive(form);
}

function sumTglPercent(form) {
  return TGL_PCT_KEYS.reduce((acc, k) => acc + (parseInt(form[k], 10) || 0), 0);
}

/**
 * @param {Array<Record<string, unknown>>} sections
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
function virkesRowsReadyForNextStep(sections) {
  if (!sections.length) {
    return {
      ok: false,
      message:
        'Lägg till minst en avdelning med virkesdata innan du går vidare, eller hämta virkesdata för valda områden.'
    };
  }
  for (const row of sections) {
    const form = sectionToForm(row);
    const vol = Number(form.volumeM3sk);
    if (!Number.isFinite(vol) || vol <= 0) {
      return {
        ok: false,
        message:
          'Ange totalvolym (m³sk) större än noll för varje avdelning och fyll i trädslagsfördelning så att summan blir 100 %.'
      };
    }
    const ha = Number(form.area);
    if (!Number.isFinite(ha) || ha <= 0) {
      return {
        ok: false,
        message: 'Ange areal större än noll för varje avdelning.'
      };
    }
    if (hasNegativeAreaOrVolume(form)) {
      return { ok: false, message: 'Areal och volym får inte vara negativa.' };
    }
    if (hasNegativeTglPercent(form)) {
      return { ok: false, message: 'Trädslagsprocent får inte vara negativa.' };
    }
    if (sumTglPercent(form) !== 100) {
      return {
        ok: false,
        message: 'Trädslagsfördelningen ska summera till 100 % för varje avdelning.'
      };
    }
  }
  return { ok: true };
}

function formToRow(form, existingId, sectionsForNextId = []) {
  const parseNum = (v) => (v === '' || v == null ? 0 : parseFloat(String(v).replace(',', '.')) || 0);
  const parseIntOrEmpty = (v) => {
    if (v === '' || v == null) return '';
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? '' : n;
  };
  return {
    id: existingId ?? nextSellTimberSectionId(sectionsForNextId),
    name: form.name.trim() || String(existingId ?? ''),
    area: roundHa(parseNum(form.area), { invalidAsZero: true }),
    volumeM3sk: parseNum(form.volumeM3sk),
    volumeM3fub: parseNum(form.volumeM3fub),
    tall: parseInt(form.tall, 10) || 0,
    gran: parseInt(form.gran, 10) || 0,
    lov: parseInt(form.lov, 10) || 0,
    al: parseIntOrEmpty(form.al),
    asp: parseIntOrEmpty(form.asp),
    björk: parseIntOrEmpty(form.björk),
    bok: parseIntOrEmpty(form.bok),
    ek: parseIntOrEmpty(form.ek)
  };
}

export default function SellTimberStep3({ wizard, dispatch }) {
  const selectedProperty = wizard.adCreateData?.selectedProperty ?? null;
  const mapSelection = wizard.geometry;
  const sections = wizard.forestWithSections?.sections ?? [];
  const setSections = useCallback((updater) => {
    dispatch({ type: 'SET_FOREST_WITH_SECTIONS', payload: updater });
  }, [dispatch]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());
  const [tglError, setTglError] = useState(false);
  const [negativePctError, setNegativePctError] = useState(false);
  /** Negative area/volume or missing/zero m³sk — drives the shared error message in the modal. */
  const [modalAreaVolumeError, setModalAreaVolumeError] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [continueError, setContinueError] = useState(null);

  /** Once true in-session, do not auto-fill skeleton again (e.g. after user removed all rows). Resets on unmount. */
  const prepopulateDoneRef = useRef(false);

  useEffect(() => {
    if (sections.length > 0) {
      prepopulateDoneRef.current = true;
      return;
    }
    if (prepopulateDoneRef.current) return;
    let cancelled = false;
    void (async () => {
      const skeleton = await buildSkeletonVirkesRowsFromMapSelection(mapSelection);
      if (cancelled) return;
      if (skeleton.length === 0) return;
      setSections(skeleton);
      prepopulateDoneRef.current = true;
    })();
    return () => {
      cancelled = true;
    };
  }, [mapSelection, sections.length, setSections]);

  useEffect(() => {
    setContinueError(null);
  }, [sections.length]);

  const featuresToFetch = useMemo(() => collectFeaturesFromMapSelection(mapSelection), [mapSelection]);
  const fetchAreaCount = featuresToFetch.length;

  const showExtraCols = sectionsUseExtraSpecies(sections);

  const fetchFromMapShapes = useCallback(async () => {
    if (featuresToFetch.length === 0) {
      setFetchError(
        'Inga områden att hämta data för. Gå tillbaka till föregående steg och välj avdelningar, avverkningsanmälningar, rita polygoner eller säkerställ att annonsutkastets geometri finns kvar på kartan.'
      );
      return;
    }
    setFetchLoading(true);
    setFetchError(null);
    try {
      const bundleSections = mapSelection?.sections ?? [];
      const rows = [];
      for (let i = 0; i < featuresToFetch.length; i++) {
        const feature = featuresToFetch[i];
        const nameFromMapSelection = feature?.properties?.name;
        if (feature?.properties?.source === 'section' && feature.properties.localID != null) {
          const sect = bundleSections.find(
            (s) => String(s.localID) === String(feature.properties.localID)
          );
          if (sect) {
            rows.push(sbpSectionToVirkesRow(sect, rows, nameFromMapSelection));
            continue;
          }
        }
        const resp = await getShapeInfo(feature);
        if (!resp.ok || !resp.data) {
          setFetchError('Kunde inte hämta virkesdata för de valda områdena.');
          return;
        }
        rows.push(mapShapeInfoDataToRow(resp.data, rows, nameFromMapSelection));
      }
      setSections(rows);
    } catch (e) {
      console.error(e);
      setFetchError('Kunde inte hämta virkesdata.');
    } finally {
      setFetchLoading(false);
    }
  }, [featuresToFetch, mapSelection?.sections, setSections]);

  const openAdd = () => {
    setModalMode('add');
    setEditingId(null);
    setForm(emptyForm());
    setTglError(false);
    setNegativePctError(false);
    setModalAreaVolumeError(false);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode('edit');
    setEditingId(row.id);
    setForm(sectionToForm(row));
    setTglError(false);
    setNegativePctError(false);
    setModalAreaVolumeError(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTglError(false);
    setNegativePctError(false);
    setModalAreaVolumeError(false);
  };

  const syncVolumeM3skFromFub = (m3fubStr) => {
    const fub = parseFloat(String(m3fubStr).replace(',', '.'));
    if (Number.isNaN(fub)) return '';
    return String(Math.round(fub / 0.83));
  };

  const syncVolumeM3fubFromSk = (m3skStr) => {
    const sk = parseFloat(String(m3skStr).replace(',', '.'));
    if (Number.isNaN(sk)) return '';
    return String(Math.round(sk * 0.83));
  };

  const handleFormField = (key, value) => {
    setModalAreaVolumeError(false);
    setNegativePctError(false);
    setTglError(false);
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'volumeM3sk') {
        next.volumeM3fub = syncVolumeM3fubFromSk(value);
      } else if (key === 'volumeM3fub') {
        next.volumeM3sk = syncVolumeM3skFromFub(value);
      }
      return next;
    });
  };

  const saveModal = (e) => {
    e.preventDefault();
    if (hasModalAreaVolumeValidationError(form)) {
      setModalAreaVolumeError(true);
      setNegativePctError(false);
      setTglError(false);
      return;
    }
    if (hasNegativeTglPercent(form)) {
      setNegativePctError(true);
      setModalAreaVolumeError(false);
      setTglError(false);
      return;
    }
    const sum = sumTglPercent(form);
    if (sum !== 100) {
      setTglError(true);
      setNegativePctError(false);
      setModalAreaVolumeError(false);
      return;
    }
    setTglError(false);
    setNegativePctError(false);
    setModalAreaVolumeError(false);
    const row = formToRow(form, editingId, sections);
    if (modalMode === 'add') {
      setSections((prev) => [...prev, row]);
    } else {
      setSections((prev) => prev.map((s) => (s.id === editingId ? row : s)));
    }
    closeModal();
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setSections((prev) => prev.filter((s) => s.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  const virkesLabelsForMap = sections.map((r) => (r?.name != null ? String(r.name) : ''));

  return (
    <>
      <TailwindWrapper>
        <div className="w-160 max-w-full mx-auto">
          <div className="mx-auto min-w-full flex max-w-full flex-col gap-6 p-6">
            <div className="flex flex-wrap items-center gap-2">
              <Text variant="text-xl/leading-normal/medium" as="span">
                Virkesinformation
              </Text>
              <SellTimberFieldInfo
                title="Virkesinformation"
                text={SELL_TIMBER_FIELD_INFO.step3VirkesData}
                aria-label="Information om volym och trädslag"
              />
            </div>

            {selectedProperty?.designation ? (
              <Text variant="text-base/leading-normal/normal" className="capitalize text-charcoal-700">
                Fastighet: {selectedProperty.designation}
              </Text>
            ) : null}

            <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
              Fyll i tabellen manuellt eller hämta avdelningsuppgifter automatiskt. Du kan lägga till, redigera eller ta bort avdelningsuppgifter
              i detta steg.
            </Text>

            {fetchAreaCount > 0 ? (
              <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
                Treebula kan automatiskt hämta avdelningsuppgifter för {fetchAreaCount} område(n). Klicka på knappen nedan om du vill använda dessa.
              </Text>
            ) : (
              <Text variant="text-sm/leading-normal/normal" className="text-amber-800">
                Du har inga områden att hämta virkesdata för än (avdelningar, avverkningsytor, ritade områden). Gå tillbaka till kartan om du vill använda hämta-knappen.
              </Text>
            )}

            <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
              Kartan visar dina val från föregående steg. Etiketter motsvarar avdelningsnamnen i tabellen nedan (vid utzoomning
              döljs etiketter tills du zoomat in).
            </Text>
          </div>
        </div>
      </TailwindWrapper>

      {mapSelection ? (
        <div
          style={{
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            padding: '0 24px 24px'
          }}
        >
          <SellTimberMap
            selection={{
              mapMode: mapSelection.mapMode ?? 'sections',
              sections: mapSelection.sections ?? [],
              borderCoords: mapSelection.borderCoords ?? null,
              fellingApplicationsGeojson: mapSelection.fellingApplicationsGeojson ?? null,
              selectedSectionLocalIds: mapSelection.selectedSectionLocalIds ?? [],
              selectedFellingApplicationIds: mapSelection.selectedFellingApplicationIds ?? [],
              drawnPolygons: mapSelection.drawnPolygons ?? [],
              placedMarkers: excludeGrotMarkerFromTimberPlacedMarkers(
                mapSelection.placedMarkers ?? []
              ),
              customAreaFromAd: mapSelection.customAreaFromAd ?? null
            }}
            handlers={{}}
            options={{
              readOnly: true,
              onlySelectedOverlays: true,
              mapId: 'sell_timber_step3_leaflet',
              hydratedAdGeometry: mapSelection.customAreaFromAd ?? null,
              virkesLabels: virkesLabelsForMap,
              mapLabelMinZoom: SELL_TIMBER_VIRKES_LABEL_MIN_ZOOM_STEP3
            }}
          />
        </div>
      ) : (
        <TailwindWrapper>
          <div className="mx-auto max-w-full px-6 pb-4">
            <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
              Ingen kartdata att visa.
            </Text>
          </div>
        </TailwindWrapper>
      )}

      <TailwindWrapper>
        <div className="w-160 max-w-full mx-auto">
          <div className="mx-auto min-w-full flex max-w-full flex-col gap-6 p-6">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="primary"
                size="medium"
                disabled={fetchLoading || fetchAreaCount === 0}
                onClick={fetchFromMapShapes}
              >
                {fetchLoading ? 'Hämtar…' : 'Hämta virkesdata för valda områden'}
              </Button>
              <Button type="button" variant="secondary" size="medium" onClick={openAdd}>
                Lägg till avdelning
              </Button>
            </div>

            {fetchError ? (
              <Text variant="text-sm/leading-normal/normal" className="text-red-700">
                {fetchError}
              </Text>
            ) : null}

            <SellTimberVirkesResponsiveTable
              rows={sections}
              showExtraCols={showExtraCols}
              formatHa={(v) => formatSvLocaleNumber(v, { maximumFractionDigits: 1 })}
              formatVolumeM3fub={(v) => formatSvLocaleNumber(v, { maximumFractionDigits: 0 })}
              pct={(v) =>
                formatSvLocaleNumber(v, { maximumFractionDigits: 0, suffix: '%', invalidAsEmpty: true })
              }
              emptyMessage="Inga rader ännu. Hämta data eller lägg till en avdelning."
              onEditRow={openEdit}
              onDeleteRow={setDeleteTarget}
            />

            {continueError ? (
              <Text variant="text-sm/leading-normal/normal" className="text-red-700" role="alert">
                {continueError}
              </Text>
            ) : null}

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="tertiary"
                onClick={() => dispatch({ type: 'SET_STEP', step: 2 })}
              >
                Tillbaka
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => {
                  const check = virkesRowsReadyForNextStep(sections);
                  if (!check.ok) {
                    setContinueError(check.message);
                    return;
                  }
                  setContinueError(null);
                  dispatch({ type: 'SET_STEP', step: 4 });
                }}
              >
                Gå vidare
              </Button>
            </div>
          </div>
        </div>

      <Dialog isOpen={modalOpen} onClose={closeModal} size="large">
        <Dialog.Header>
          <Text variant="text-lg/leading-normal/medium">
            {modalMode === 'add' ? 'Lägg till avdelning' : 'Redigera avdelning'}
          </Text>
        </Dialog.Header>
        <Dialog.Content>
          <form className="flex flex-col gap-4" onSubmit={saveModal}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                variant="fluid"
                label="Avdelningsnummer / namn"
                type="text"
                value={form.name}
                onChange={(v) => handleFormField('name', v)}
              />
              <Input
                variant="fluid"
                label="Areal (ha)"
                type="number"
                value={form.area}
                onChange={(v) => handleFormField('area', v)}
                isValid={isNegativeNumberFieldValue(form.area) ? false : undefined}
              />
              <Input
                variant="fluid"
                label="Totalvolym (m³sk)"
                type="number"
                value={form.volumeM3sk}
                onChange={(v) => handleFormField('volumeM3sk', v)}
                isValid={
                  isNegativeNumberFieldValue(form.volumeM3sk) ||
                  (modalAreaVolumeError && isVolumeM3skNotPositive(form))
                    ? false
                    : undefined
                }
              />
              <Input
                variant="fluid"
                label="Totalvolym (m³fub)"
                type="number"
                value={form.volumeM3fub}
                onChange={(v) => handleFormField('volumeM3fub', v)}
                isValid={isNegativeNumberFieldValue(form.volumeM3fub) ? false : undefined}
              />
            </div>
            {modalAreaVolumeError ? (
              <Text variant="text-sm/leading-normal/normal" className="text-red-700" role="alert">
                Du måste fylla i volym och areal
              </Text>
            ) : null}

            <Text variant="text-sm/leading-normal/medium">Trädslagsfördelning (TGL) - summan ska vara 100%</Text>
            {negativePctError ? (
              <Text variant="text-sm/leading-normal/normal" className="text-red-700" role="alert">
                Procent får inte vara negativa.
              </Text>
            ) : null}
            {tglError ? (
              <Text variant="text-sm/leading-normal/normal" className="text-red-700" role="alert">
                Summan av procenten är inte 100.
              </Text>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                variant="fluid"
                label="Tall %"
                type="number"
                value={form.tall}
                onChange={(v) => handleFormField('tall', v)}
                isValid={isNegativeNumberFieldValue(form.tall) ? false : undefined}
              />
              <Input
                variant="fluid"
                label="Gran %"
                type="number"
                value={form.gran}
                onChange={(v) => handleFormField('gran', v)}
                isValid={isNegativeNumberFieldValue(form.gran) ? false : undefined}
              />
              <Input
                variant="fluid"
                label="Löv %"
                type="number"
                value={form.lov}
                onChange={(v) => handleFormField('lov', v)}
                isValid={isNegativeNumberFieldValue(form.lov) ? false : undefined}
              />
            </div>

            <Text variant="text-sm/leading-normal/normal" className="text-charcoal-600">
              Frivilliga trädslag (räknas in i samma 100 %)
            </Text>
            <div className="grid gap-4 sm:grid-cols-3">
              {SELL_TIMBER_EXTRA_SPECIES.map(({ key, label }) => (
                <Input
                  key={key}
                  variant="fluid"
                  label={`${label} %`}
                  type="number"
                  value={form[key]}
                  onChange={(v) => handleFormField(key, v)}
                  isValid={isNegativeNumberFieldValue(form[key]) ? false : undefined}
                />
              ))}
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button type="submit" variant="primary">
                Spara
              </Button>
              <Button type="button" variant="secondary" onClick={closeModal}>
                Avbryt
              </Button>
            </div>
            
          </form>
        </Dialog.Content>
      </Dialog>

      <Dialog isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} size="small">
        <Dialog.Header>
          <Text variant="text-lg/leading-normal/medium">Ta bort avdelning?</Text>
        </Dialog.Header>
        <Dialog.Content>
          <Text variant="text-sm/leading-normal/normal">
            {deleteTarget
              ? `Vill du ta bort avdelning ${deleteTarget.name}? Detta går inte att ångra.`
              : ''}
          </Text>
        </Dialog.Content>
        <Dialog.Footer>
          <div className="flex flex-wrap gap-3">
            <Button type="button" variant="tertiary" onClick={() => setDeleteTarget(null)}>
              Avbryt
            </Button>
            <Button type="button" variant="primary" onClick={confirmDelete}>
              Ta bort
            </Button>
          </div>
        </Dialog.Footer>
      </Dialog>
      </TailwindWrapper>
    </>
  );
}
